// src/features/ucits-etf/api/etfQueries.ts
//
// TanStack Query hooks for the UCITS-ETF feature. We deliberately go
// through the shared `apiClient` axios instance (NOT a bare axios call)
// because:
//   - it attaches the WordPress-issued JWT (Authorization: Bearer <token>)
//     so the auth-gated /api/v1/ucits-etfs router is reached as an
//     authorised user (a raw fetch would 401), and
//   - it already bakes the `/api/v1` version prefix into its baseURL, so we
//     request the bare `/ucits-etfs/...` path here.
//
// The backend serves every read entirely from an in-memory Polars frame
// (warmed at app startup) + a Numba cosine kernel, so responses are
// sub-millisecond. We layer aggressive client-side caching on top via
// staleTime so navigating around the dashboard feels instantaneous.
//
// Envelope handling: the backend wraps list payloads in an object that
// also carries `execution_time_ms`. These hooks unwrap the inner array
// (suggest / similar) or pass the whole envelope through (breakouts /
// screen) as documented per-hook.

import { useMutation, useQuery } from '@tanstack/react-query';

import { apiClient } from '../../../api/client';
import type {
    EtfDetail,
    EtfSuggestion,
    ScreenerRequest,
    ScreenerResponse,
    SimilarResponse,
    SuggestResponse,
    TopBreakoutIdeasResponse,
    TopFlowsResponse,
} from '../types';

// Bare feature path — the `/api/v1` prefix comes from apiClient.baseURL.
const BASE = '/ucits-etfs';

/**
 * Instant autocomplete on name / symbol / ISIN. Unwraps the SuggestResponse
 * envelope down to the `results` array the AutoSuggest dropdown renders.
 *
 * The backend requires min_length=1 on the query, so we gate the request on
 * a 2-char minimum here (fewer keystrokes rarely narrow anything useful and
 * would only add noise / load).
 */
export const useEtfSuggest = (query: string) => {
    const trimmed = query.trim();
    return useQuery({
        queryKey: ['ucits-etf', 'suggest', trimmed],
        queryFn: async (): Promise<EtfSuggestion[]> => {
            if (trimmed.length < 2) return [];
            const { data } = await apiClient.get<SuggestResponse>(
                `${BASE}/suggest`,
                { params: { query: trimmed, limit: 10 } },
            );
            return data.results;
        },
        enabled: trimmed.length >= 2,
        // Search results change only nightly — cache generously.
        staleTime: 1000 * 60 * 5,
        // Keep the previous results visible while the next query resolves so
        // the dropdown does not flash empty on every keystroke.
        placeholderData: (prev) => prev,
    });
};

/**
 * Top-N quantitative BUY / SELL breakout ideas. Returns the full envelope
 * (both `top_buys` and `top_sells`) so the board can split it into columns.
 * Data only changes nightly, so we cache for an hour.
 */
export const useBreakoutIdeas = (limit = 20) => {
    return useQuery({
        queryKey: ['ucits-etf', 'breakouts', limit],
        queryFn: async (): Promise<TopBreakoutIdeasResponse> => {
            const { data } = await apiClient.get<TopBreakoutIdeasResponse>(
                `${BASE}/breakout-ideas`,
                { params: { limit } },
            );
            return data;
        },
        staleTime: 1000 * 60 * 60,
    });
};

/**
 * Top-N inferred capital inflows / outflows. Returns the full envelope
 * (both `top_inflows` and `top_outflows`) so the dashboard can render the two
 * Recharts panels and the unified explorer table. Flows only change nightly,
 * so we cache for an hour like the breakout board.
 */
export const useTopFlows = (limit = 20) => {
    return useQuery({
        queryKey: ['ucits-etf', 'top-flows', limit],
        queryFn: async (): Promise<TopFlowsResponse> => {
            const { data } = await apiClient.get<TopFlowsResponse>(
                `${BASE}/top-flows`,
                { params: { limit } },
            );
            return data;
        },
        staleTime: 1000 * 60 * 60,
    });
};

/**
 * Distinct sector names present across the loaded universe — drives the
 * screener's sector multi-select. The list is tiny and static per nightly
 * sync, so it is cached for the whole session.
 */
export const useEtfSectors = () => {
    return useQuery({
        queryKey: ['ucits-etf', 'sectors'],
        queryFn: async (): Promise<string[]> => {
            const { data } = await apiClient.get<string[]>(`${BASE}/sectors`);
            return data;
        },
        staleTime: Infinity,
    });
};

/**
 * The observed dividend-policy vocabulary (`["Accumulating", "Distributing"]`)
 * served by GET /ucits-etfs/dividend-policies. Drives the screener's policy
 * filter. The list is static per deploy so it is cached for the whole
 * session; on the rare chance the request fails we still degrade gracefully
 * because the consumer supplies the same two literals as a default.
 */
export const useDividendPolicies = () => {
    return useQuery({
        queryKey: ['ucits-etf', 'dividend-policies'],
        queryFn: async (): Promise<string[]> => {
            const { data } = await apiClient.get<string[]>(
                `${BASE}/dividend-policies`,
            );
            return data;
        },
        staleTime: Infinity,
    });
};

/**
 * Run the in-memory screener. A mutation (not a query) because the filter
 * payload is user-driven and POSTed on demand. Returns the full
 * ScreenerResponse so the caller can render `total_matches` +
 * `execution_time_ms` alongside the rows.
 */
export const useEtfScreener = () => {
    return useMutation({
        mutationFn: async (
            filters: ScreenerRequest,
        ): Promise<ScreenerResponse> => {
            const { data } = await apiClient.post<ScreenerResponse>(
                `${BASE}/screen`,
                filters,
            );
            return data;
        },
    });
};

/**
 * Sector-overlap peers for a symbol (Numba cosine kernel). Unwraps the
 * SimilarResponse envelope to the `results` array. Only runs when a symbol
 * is selected (the similarity modal is closed otherwise).
 */
export const useSimilarEtfs = (symbol: string | null) => {
    return useQuery({
        queryKey: ['ucits-etf', 'similar', symbol],
        queryFn: async (): Promise<EtfDetail[]> => {
            if (!symbol) return [];
            const { data } = await apiClient.get<SimilarResponse>(
                `${BASE}/${encodeURIComponent(symbol)}/similar`,
                { params: { limit: 10, only_ucits: true } },
            );
            return data.results;
        },
        enabled: Boolean(symbol),
        staleTime: 1000 * 60 * 60,
    });
};
